import React, { useState, useEffect, useRef } from 'react';

function App() {
  const [status, setStatus] = useState('loading'); // loading, ready, processing, error
  const [progressText, setProgressText] = useState('');
  const [image, setImage] = useState(null);
  const [result, setResult] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  
  const workerRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    // Initialize Web Worker to run AI in background thread
    workerRef.current = new Worker(new URL('./worker.js', import.meta.url));

    workerRef.current.onmessage = (event) => {
      const { status, error, result, progress } = event.data;
      
      if (status === 'loading') setStatus('loading');
      else if (status === 'ready') {
          setStatus('ready');
          setProgressText('');
      }
      else if (status === 'processing') setStatus('processing');
      else if (status === 'progress') {
          if (progress && progress.status === 'downloading') {
              setProgressText(`Downloading AI Model: ${Math.round(progress.progress)}%`);
          }
      }
      else if (status === 'complete') {
        // AI execution is done! Reconstruct the image from the worker's output
        const { data, width, height } = result;
        
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        
        // Convert RGB (3 channels) back to RGBA (4 channels) for the Canvas
        const imageDataArray = new Uint8ClampedArray(width * height * 4);
        for (let i = 0; i < width * height; i++) {
            imageDataArray[i * 4] = data[i * 3];       // R
            imageDataArray[i * 4 + 1] = data[i * 3 + 1]; // G
            imageDataArray[i * 4 + 2] = data[i * 3 + 2]; // B
            imageDataArray[i * 4 + 3] = 255;             // Alpha
        }
        
        const imageData = new ImageData(imageDataArray, width, height);
        ctx.putImageData(imageData, 0, 0);
        
        setResult(canvas.toDataURL("image/png"));
        setStatus('ready');
      } else if (status === 'error') {
        console.error(error);
        alert('Worker Error: ' + error);
        setStatus('ready');
      }
    };

    // Tell worker to load the model
    workerRef.current.postMessage({ action: 'init' });

    return () => {
        if (workerRef.current) workerRef.current.terminate();
    };
  }, []);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = (file) => {
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }
    
    const url = URL.createObjectURL(file);
    setImage(url);
    setResult(null); // Reset previous result
  };

  const startUpscaling = async () => {
    if (!workerRef.current || !image) return;
    setStatus('processing');
    
    // 1. Resize image safely on main thread to avoid Out-Of-Memory
    const imgElem = document.createElement('img');
    imgElem.src = image;
    await new Promise(resolve => { imgElem.onload = resolve; });

    const MAX_SIZE = 256;
    let { width, height } = imgElem;
    if (width > MAX_SIZE || height > MAX_SIZE) {
      const ratio = Math.min(MAX_SIZE / width, MAX_SIZE / height);
      width = Math.round(width * ratio);
      height = Math.round(height * ratio);
    }
    
    // Ensure multiples of 8 (Swin2SR strict requirement)
    width = Math.floor(width / 8) * 8;
    height = Math.floor(height / 8) * 8;

    const offscreenCanvas = document.createElement('canvas');
    offscreenCanvas.width = width;
    offscreenCanvas.height = height;
    const ctx = offscreenCanvas.getContext('2d');
    ctx.drawImage(imgElem, 0, 0, width, height);
    
    // 2. Extract raw RGBA pixel data
    const imageData = ctx.getImageData(0, 0, width, height);

    // 3. Send pixel data to the BACKGROUND WORKER (so the page doesn't freeze!)
    workerRef.current.postMessage({
        action: 'upscale',
        payload: {
            data: imageData.data,
            width,
            height
        }
    });
  };

  const cancelSelection = () => {
    setImage(null);
    setResult(null);
  };

  const getStatusText = () => {
    switch(status) {
      case 'loading': return progressText || 'Downloading AI Model (Takes a minute on first load)...';
      case 'ready': return 'AI Model Ready. Upload an image.';
      case 'processing': return 'Upscaling in background... UI remains responsive! ✨';
      case 'error': return 'Failed to load model. Check console.';
      default: return '';
    }
  };

  return (
    <div className="app-container">
      <header className="header">
        <h1>UltraPixel</h1>
        <p>100% Client-Side AI Image Upscaling. No Server Needed.</p>
      </header>

      <main className="glass-panel">
        <div style={{ textAlign: 'center' }}>
          <div className={`status-badge status-${status}`}>
            {getStatusText()}
          </div>
        </div>

        {!image ? (
          <div 
            className={`upload-area ${isDragging ? 'drag-active' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input 
              type="file" 
              ref={fileInputRef} 
              style={{ display: 'none' }} 
              accept="image/*"
              onChange={handleFileInput}
            />
            <span className="upload-icon">✨</span>
            <h3>Drag & Drop your image here</h3>
            <p style={{ color: 'var(--text-muted)' }}>or click to browse from your computer</p>
          </div>
        ) : (
          <div style={{ textAlign: 'center' }}>
            <div style={{ display: 'flex', gap: '2rem', justifyContent: 'center', margin: '2rem 0', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 300px' }}>
                <h4 style={{ marginBottom: '1rem', color: 'var(--text-muted)' }}>Original (Low-Res)</h4>
                <img src={image} alt="Original" style={{ maxWidth: '100%', maxHeight: '400px', borderRadius: '0.5rem', border: '1px solid rgba(255,255,255,0.2)', objectFit: 'contain' }} />
              </div>
              
              {result && (
                <div style={{ flex: '1 1 300px' }}>
                  <h4 style={{ marginBottom: '1rem', color: '#4ade80' }}>Upscaled (High-Res)</h4>
                  <img src={result} alt="Upscaled" style={{ maxWidth: '100%', maxHeight: '400px', borderRadius: '0.5rem', border: '2px solid #4ade80', objectFit: 'contain', boxShadow: '0 0 20px rgba(74, 222, 128, 0.2)' }} />
                </div>
              )}
            </div>
            
            {!result ? (
               <button 
                className="btn-primary" 
                onClick={startUpscaling}
                disabled={status !== 'ready'}
              >
                {status === 'processing' ? 'Upscaling...' : 'Start Upscaling ✨'}
              </button>
            ) : (
               <a 
                 href={result} 
                 download="upscaled-image.png"
                 className="btn-primary" 
                 style={{ display: 'inline-block', textDecoration: 'none' }}
               >
                 Download Upscaled Image 💾
               </a>
            )}
            
            <button 
              className="btn-primary" 
              style={{ background: 'transparent', border: '1px solid var(--text-muted)', marginLeft: '1rem' }}
              onClick={cancelSelection}
              disabled={status === 'processing'}
            >
              {result ? 'Upscale Another' : 'Cancel'}
            </button>
          </div>
        )}
      </main>
    </div>
  );
}

export default App;
